import React from 'react';
import MaterialTable from 'material-table';
import { createTheme, MuiThemeProvider } from '@material-ui/core/styles';
import Modal from '@material-ui/core/Modal';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';

// MUI transitions
import Fade from '@material-ui/core/Fade';
import Backdrop from '@material-ui/core/Backdrop';
import { MenuItem, TextField } from '@material-ui/core';
import MonthYearPicker from '../MonthYearPicker';

export default class EhcBillingsDataTable extends React.Component {
  constructor(props) {
    super(props);
    this.tableRef = React.createRef();
    this.theme = createTheme({
      palette: {
        primary: {
          main: '#7cb342',
        },
        secondary: {
          main: '#4caf50',
        },
      },
    });

    this.baseUrl = (company_id) => {
      if (!company_id) { // If company_id is not provided, use the one from props
        company_id = this.props.company_id;
      } else if (Array.isArray(company_id)) { // If company_id is an array, do not use company path
        company_id = null;
      }

      return `/api/ehc/${company_id ? `companies/${company_id}/` : ''}billings`;
    };

    this.parseDate = (dateStr) => {
      if (!dateStr) return '';
      const [year, month, day] = dateStr.split('-').map(Number);
      const date = new Date(year, month - 1, day); // month is 0-indexed
      return date;
    };

    this.formatDate = (date) => {
      if (!date) return null;
      const res = date.getFullYear() +
        "-" + String(date.getMonth() + 1).padStart(2, "0") +
        "-" + String(date.getDate()).padStart(2, "0");
      return res;
    };

    this.displayDate = (dateStr) => {
      const date = this.parseDate(dateStr);
      return date.toLocaleString('en-US', { month: 'long', year: 'numeric' });
    };

    // This function to try to get a nested field from an object
    // Usefull for custom render of Material Table that combined with grouping,
    // When grouping, custom render is used for displaying the data the param that passed is the field directly
    // It is assuming the fields of the data is not nested
    this.tryGetRowDataField = (input, field) => {
      if (input !== null && typeof input === "object") {
        return input[field];
      }
      return input;
    }

    // Arrange columns
    let baseColumns = [
      {
        title: 'Company', field: 'company_name', editable: 'never', orderField: 'companies.name',
        render: rowData => (
          <a href={this.tryGetRowDataField(rowData, 'company_url')}>
            {this.tryGetRowDataField(rowData, 'company_name')}
          </a>
        ),
      },
      {
        title: 'Period', field: 'period',
        render: rowData => this.displayDate(this.tryGetRowDataField(rowData, 'period')),
        editComponent: props => {
          if (!props.value) props.onChange(this.formatDate(new Date())); // init value
          return (
            <MonthYearPicker
              value={props.value || new Date()}
              onChange={(selectedDate) => props.onChange(this.formatDate(selectedDate))}
              parseDate={this.parseDate}
            />
          )
        }
      },
      { title: 'Amount', field: 'amount', type: 'numeric', grouping: false,
        render: rowData => rowData.amount_s,
      },
      { title: 'Total Claim', field: 'total_claim' },
      {
        title: 'Status', field: 'status',
        render: rowData => this.props.allowed_statuses.find(s => s.value === this.tryGetRowDataField(rowData, 'status'))?.label,
        editComponent: props => (
          <TextField
            select
            value={props.value || ''}
            onChange={e => props.onChange(e.target.value)}
          >
            {this.props.allowed_statuses.map(s => (
              <MenuItem key={s.value} value={s.value}>
                {s.label}
              </MenuItem>
            ))}
          </TextField>
        )
      },
      {
        title: 'Invoice', field: 'invoice_document', editable: 'never', grouping: false,
        render: rowData => rowData.invoice_document_url
          ? <a href={rowData.invoice_document_url} target="_blank" rel="noopener noreferrer">{rowData.invoice_document_name}</a>
          : "-",
      },
      { title: 'Created at', field: 'created_at', editable: 'never', grouping: false },
    ]

    // Arrange action
    let baseActions = [
      {
        icon: 'refresh',
        tooltip: 'Refresh Data',
        isFreeAction: true,
        onClick: () =>
          this.tableRef.current && this.tableRef.current.onQueryChange(),
      },
    ]

    // When editable
    if (this.props.editable) {
      baseActions.push({
        icon: 'delete',
        tooltip: 'Delete All Selected',
        onClick: (evt, selectedRows) => this.handleOpenConfirmModal(selectedRows)
      })
      baseActions.push({
        icon: 'insert_drive_file',
        tooltip: 'Update Invoice',
        position: 'row',
        onClick: (evt, selectedRow) => this.handleOpenUploadModal(selectedRow)
      })
    }

    this.state = {
      columns: baseColumns,
      actions: baseActions,
      data: [],
      page: null,
      open: false,
      totalCount: null,

      // Modal state
      uploadModalOpen: false,
      confirmModalOpen: false,
      selectedBillings: null,

      // File-related state
      selectedFile: null,
      fileName: '',
      previewUrl: ''
    };
  }

  // =====================
  // Modal Handlers
  // =====================
  handleOpenUploadModal = (billing) => {
    this.setState({
      uploadModalOpen: true,
      confirmModalOpen: false,
      selectedBillings: [billing]
    });
  };

  handleOpenConfirmModal = (billings) => {
    this.setState({
      uploadModalOpen: false,
      confirmModalOpen: true,
      selectedBillings: billings,
    });

  };

  handleCloseModal = () => {
    this.setState({
      uploadModalOpen: false,
      confirmModalOpen: false,
      selectedBillings: null,
      selectedFile: null,
      fileName: '',
      previewUrl: ''
    });
  };

  handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Create an object URL for preview
    const previewUrl = URL.createObjectURL(file);

    this.setState({
      selectedFile: file,
      fileName: file.name,
      previewUrl: previewUrl
    });
  };

  handleDocumentUpdate = async (isRemove) => {
    const { selectedFile, selectedBillings } = this.state;
    if ((!isRemove && !selectedFile) || !selectedBillings || selectedBillings.length < 1) return;

    const formData = new FormData();
    formData.append('invoice_document', isRemove ? null : selectedFile);

    const csrfToken = document.querySelector('[name=csrf-token]').content;
    const url = `${this.baseUrl(selectedBillings[0].company_id)}/${selectedBillings[0].id}`;

    try {
      const response = await fetch(url, {
        method: 'PATCH', // or 'PUT'
        headers: {
          'X-CSRF-Token': csrfToken
        },
        body: formData
      });

      const result = await response.json();
      console.log('Invoice document update result:', result);

      if (response.ok) {
        this.handleCloseModal();
        if (this.tableRef.current) {
          this.tableRef.current.onQueryChange();
        }
      } else {
        console.error('Error updating invoice document:', result);
      }
    } catch (err) {
      console.error('Error updating invoice document:', err);
    }
  };

  // =====================
  // MaterialTable Queries
  // =====================
  getApiErrorMessage(result) {
    if (result.success) {
      return null;
    }
    if (result.errors && typeof result.errors === 'object' && !Array.isArray(result.errors)) {
      // handle validation error
      return 'Validation: ' + Object.entries(result.errors).map(([key, value]) => `${key}: ${value}`).join(', ');
    }
    console.error('Error fetching data:', result);
    if (result.message) {
      return result.message;
    }
    return 'There is something wrong';
  }

  fetchData(query) {
    return new Promise((resolve, reject) => {
      const requestedPage = query.page + 1;

      console.log('Query details:', {
        page: query.page,
        pageSize: query.pageSize,
        requestedPage: requestedPage,
        search: query.search
      });

      const baseParams =  'per_page=' + query.pageSize
        + '&q=' + query.search
        + '&sort_by=' + (query.orderBy?.orderField ?? query.orderBy?.field ?? '')
        + '&sort_order=' + (query.orderDirection ?? 'asc');
      const url = this.baseUrl() + '?' + baseParams + '&page=' + requestedPage;

      fetch(url)
        .then(async res => {
          if (res && res.status === 200 && res.ok) {
            return res.json();
          } else {
            console.log('Error response, fetching fallback data');
            const lastPageUrl = this.baseUrl() + '?' + baseParams + '&page=1';
            const fallbackRes = await fetch(lastPageUrl);
            const fallbackData = await fallbackRes.json();
            const lastPage = fallbackData.pagination.last;

            console.log('Fallback - redirecting to last page:', lastPage);
            const correctedUrl = this.baseUrl() + '?' + baseParams + '&page=' + lastPage;
            const correctedRes = await fetch(correctedUrl);
            return await correctedRes.json();
          }
        })
        .then(data => {
          console.log('Fetch response:', data);

          const currentPage = data.pagination.page - 1;
          const totalCount = data.pagination.count;
          const actualPageSize = parseInt(data.pagination.vars.items) || query.pageSize;
          const lastPage = data.pagination.last - 1;

          console.log('Pagination calculation:', {
            currentPage,
            totalCount,
            actualPageSize,
            lastPage,
            requestedPage: query.page,
            queryPageSize: query.pageSize
          });

          let adjustedTotalCount = totalCount;
          if (query.page > lastPage) {

            adjustedTotalCount = (lastPage + 1) * query.pageSize;
            console.log('Adjusted total count to prevent overflow:', adjustedTotalCount);
          }

          resolve({
            data: data.billings,
            page: currentPage,
            totalCount: adjustedTotalCount
          });
        })
        .catch(error => {
          console.error('Fetch error:', error);
          reject(error);
        });
    });
  }

  batchDelete(billings) {
    const array_keys = billings.map(billing => billing.id);
    const company_ids = [...new Set(billings.map(billing => billing.company_id))];
    const csrfToken = document.querySelector('[name=csrf-token]').content;

    return new Promise((resolve, reject) => {
      fetch(this.baseUrl(company_ids) + '/batch_delete', {
        method: 'POST',
        body: JSON.stringify({ billing_ids: array_keys }),
        headers: {
          "Content-type": "application/json; charset=UTF-8",
          'X-CSRF-Token': csrfToken
        }
      })
      .then(response => response.json())
      .then(result => {
        console.log('Batch delete result:', result);
        if (this.tableRef.current) {
          this.tableRef.current.onQueryChange();
        }
        resolve();
      })
      .catch(err => reject(err));
    });
  }

  // =====================
  // Render Modal
  // =====================
  renderUploadModal() {
    return (
      <Modal
        open={this.state.uploadModalOpen}
        onClose={this.handleCloseModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={this.state.uploadModalOpen} timeout={500}>
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            bgcolor="#fff"
            p={4}
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              outline: 'none',
              borderRadius: '4px',
              minWidth: '300px'
            }}
          >
            <Typography variant="h6" gutterBottom>
              Update Invoice Document
            </Typography>

            {/* MATERIAL-UI–STYLE FILE PICKER */}
            <Button
              variant="outlined"
              component="label"
              className="mb-4"
            >
              Choose File
              <input
                type="file"
                accept="application/pdf"
                hidden
                onChange={this.handleFileSelect}
              />
            </Button>

            {/* Show current saved file */}
            <Typography variant="body2" className="mb-4">
              Choose file and submit to update the invoice document, or click 'Delete' to remove current document.
            </Typography>
            {this.state.selectedBillings && this.state.selectedBillings.length > 0 && this.state.selectedBillings[0].invoice_document_url && (
              <Typography variant="body2" className="mb-4">
                Current: <a href={this.state.selectedBillings[0].invoice_document_url} alt="Current Invoice Preview" target="_blank">{this.state.selectedBillings[0].invoice_document_name}</a>
              </Typography>
            )}
            {/* Show the file name if present */}
            {this.state.fileName && (
              <Typography variant="body2" className="mb-4">
                Selected: <a href={this.state.previewUrl} alt="Invoice Preview" target="_blank">{this.state.fileName}</a>
              </Typography>
            )}

            <Box>
              <Button
                variant="contained"
                onClick={async () => await this.handleDocumentUpdate(true)}
                disabled={!this.state.selectedBillings || this.state.selectedBillings.length < 1 || !this.state.selectedBillings[0].invoice_document_url}
                className="mr-2"
                style={{ backgroundColor: '#d93939ff' }}
              >
                Delete
              </Button>
              <Button
                variant="outlined"
                onClick={this.handleCloseModal}
                className="mr-2"
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={async () => await this.handleDocumentUpdate(false)}
                disabled={!this.state.selectedFile}
                className="mr-2"
              >
                Submit
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>
    );
  }

  renderConfirmModal() {
    return (
      <Modal
        open={this.state.confirmModalOpen}
        onClose={this.handleCloseModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={this.state.confirmModalOpen} timeout={500}>
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            bgcolor="#fff"
            p={4}
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              outline: 'none',
              borderRadius: '4px',
              minWidth: '300px'
            }}
          >

            <Typography variant="h6" gutterBottom>
              Are you sure you want to delete all selected billings?
            </Typography>

            <Box>
              <Button
                variant="outlined"
                onClick={this.handleCloseModal}
                className="mr-2"
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => this.batchDelete(this.state.selectedBillings).then(() => this.handleCloseModal()).then(() => this.setState({ open: true }))}
                style={{ backgroundColor: '#d93939ff' }}
              >
                Confirm
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>
    );
  }

  // =====================
  // Render Component
  // =====================
  render() {
    return (
      <MuiThemeProvider theme={this.theme}>
        {this.props.editable && this.renderUploadModal()}
        {this.props.editable && this.renderConfirmModal()}
        <MaterialTable
          title={this.props.title}
          columns={this.state.columns}
          tableRef={this.tableRef}
          actions={this.state.actions}
          options={{
            pageSize: 20,
            pageSizeOptions: [20, 30, 50],
            exportButton: true,
            sorting: true,
            search: true,
            grouping: true,
            selection: this.props.editable,
            addRowPosition: 'first',
            // Force MaterialTable to respect the pagination boundaries
            emptyRowsWhenPaging: false,
            // This will help ensure proper pagination behavior
            paginationType: 'normal'
          }}
          data={query =>
            this.fetchData(query).then(result => {
              // Update component state to track current pagination
              this.setState({
                currentPage: result.page,
                totalCount: result.totalCount
              });
              return result;
            })
          }
          editable={ this.props.editable ? {
            onRowAdd: newData =>
              new Promise((resolve, reject) => {
                const csrfToken = document.querySelector('[name=csrf-token]').content;
                fetch(this.baseUrl(newData.company_id) + '/', {
                  method: 'POST',
                  body: JSON.stringify(newData),
                  headers: {
                    "Content-type": "application/json; charset=UTF-8",
                    'X-CSRF-Token': csrfToken
                  }
                })
                .then(response => response.json())
                .then(result => {
                  const errMsg = this.getApiErrorMessage(result);
                  if (errMsg) {
                    this.setState({ error: errMsg });
                    return reject(errMsg);
                  }
                  resolve();
                  if (this.tableRef.current) {
                    this.tableRef.current.onQueryChange();
                  }
                })
                .catch(err => reject(err));
              }),

            onRowUpdate: (newData, oldData) =>
              new Promise((resolve, reject) => {
                const csrfToken = document.querySelector('[name=csrf-token]').content;
                fetch(`${this.baseUrl(newData.company_id)}/${newData.id}`, {
                  method: 'PUT',
                  body: JSON.stringify(newData),
                  headers: {
                    "Content-type": "application/json; charset=UTF-8",
                    'X-CSRF-Token': csrfToken
                  }
                })
                .then(response => response.json())
                .then(result => {
                  const errMsg = this.getApiErrorMessage(result);
                  if (errMsg) {
                    this.setState({ error: errMsg });
                    return reject(errMsg);
                  }
                  console.log('Update result:', result);
                  resolve();
                  if (this.tableRef.current) {
                    this.tableRef.current.onQueryChange();
                  }
                })
                .catch(err => reject(err));
              }),

            onRowDelete: oldData =>
              new Promise((resolve, reject) => {
                const csrfToken = document.querySelector('[name=csrf-token]').content;
                fetch(`${this.baseUrl(oldData.company_id)}/${oldData.id}`, {
                  method: 'DELETE',
                  headers: {
                    "Content-type": "application/json; charset=UTF-8",
                    'X-CSRF-Token': csrfToken
                  }
                })
                .then(response => response.json())
                .then(result => {
                  const errMsg = this.getApiErrorMessage(result);
                  if (errMsg) {
                    this.setState({ error: errMsg });
                    return reject(errMsg);
                  }
                  console.log('Delete result:', result);
                  resolve();
                  if (this.tableRef.current) {
                    this.tableRef.current.onQueryChange();
                  }
                })
                .catch(err => reject(err));
              }),
          } : {}}
        />
        <span style={{ color: 'red' }} hidden={!this.state.error}>* { this.state.error }</span>
      </MuiThemeProvider>
    );
  }
}
